/**
 * `navSections` — shared navigation registry + inline icons.
 *
 * Single source of truth for the app's nav structure, consumed by both
 * the desktop `<Sidebar>` and the mobile `<MobileNav>` (drawer + bottom
 * tab bar). Extracted from Sidebar.tsx so the two chrome variants can't
 * drift apart.
 */
import type { ReactNode } from 'react';

export interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  icon: ReactNode;
  /** Optional small badge (e.g. unread count). Currently unused. */
  badge?: string | number;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

/* ---------- Inline lucide-style icons (no extra deps) -------------- */
function Icon({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {children}
    </svg>
  );
}
export const IconApex = () => (
  <Icon>
    <path d="M3 12s3-7 9-7 9 7 9 7-3 7-9 7-9-7-9-7z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);
export const IconMonitor = () => (
  <Icon>
    <path d="M3 12h4l2-7 4 14 2-7h6" />
  </Icon>
);
export const IconLog = () => (
  <Icon>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M9 13h6" />
    <path d="M9 17h4" />
  </Icon>
);
export const IconBell = () => (
  <Icon>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </Icon>
);
export const IconTasks = () => (
  <Icon>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="m9 11 2 2 4-4" />
    <path d="M9 17h6" />
  </Icon>
);
export const IconChart = () => (
  <Icon>
    <path d="M3 3v18h18" />
    <path d="m19 9-5 5-4-4-3 3" />
  </Icon>
);
export const IconModules = () => (
  <Icon>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </Icon>
);
export const IconSources = () => (
  <Icon>
    <rect x="9" y="2" width="6" height="6" rx="1" />
    <rect x="2" y="16" width="6" height="6" rx="1" />
    <rect x="16" y="16" width="6" height="6" rx="1" />
    <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />
    <path d="M12 8v4" />
  </Icon>
);
export const IconSettings = () => (
  <Icon>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Icon>
);
export const IconBeaker = () => (
  <Icon>
    <path d="M8 2h8v3l4 10H4L8 5V2z" />
    <path d="M8 2v3" />
    <line x1="9" y1="11" x2="15" y2="11" />
    <line x1="10" y1="14" x2="14" y2="14" />
  </Icon>
);
export const IconFlask = () => (
  <Icon>
    <path d="M9 3h6v4l4 10H5L9 7V3z" />
    <path d="M9 3v4" />
    <path d="M6 14h12" />
  </Icon>
);
export const IconDev = () => (
  <Icon>
    <path d="m16 18 6-6-6-6" />
    <path d="m8 6-6 6 6 6" />
  </Icon>
);
export const IconChevronLeft = () => (
  <Icon>
    <path d="m15 18-6-6 6-6" />
  </Icon>
);
export const IconChevronRight = () => (
  <Icon>
    <path d="m9 18 6-6-6-6" />
  </Icon>
);
export const IconWaves = () => (
  <Icon>
    <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2C21 5 21.5 5.5 22 6" />
    <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
    <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
  </Icon>
);
export const IconBolt = () => (
  <Icon>
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
  </Icon>
);
export const IconCalendar = () => (
  <Icon>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </Icon>
);
export const IconDroplet = () => (
  <Icon>
    <path d="M12 2.7s6 5.5 6 10a6 6 0 0 1-12 0c0-4.5 6-10 6-10z" />
  </Icon>
);
export const IconNote = () => (
  <Icon>
    <path d="M4 4a2 2 0 0 1 2-2h9l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    <path d="M14 2v6h6" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </Icon>
);
export const IconPool = () => (
  <Icon>
    <circle cx="12" cy="6" r="3" />
    <path d="M2 14c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
    <path d="M2 19c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
  </Icon>
);
export const IconCoral = () => (
  <Icon>
    <path d="M12 22v-7" />
    <path d="M12 15c0-3-2-4-2-7a2 2 0 0 1 4 0c0 3-2 4-2 7z" />
    <path d="M8 18c-1.5-1-2-3-2-5a2 2 0 0 1 4 0" />
    <path d="M16 18c1.5-1 2-3 2-5a2 2 0 0 0-4 0" />
  </Icon>
);
export const IconArchive = () => (
  <Icon>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </Icon>
);

export const IconMenu = () => (
  <Icon>
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </Icon>
);
export const IconClose = () => (
  <Icon>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </Icon>
);

/* ---------- Section / item registry --------------------------------- */
export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Live',
    items: [
      { to: '/', label: 'Monitor', end: true, icon: <IconMonitor /> },
      { to: '/apex', label: 'Apex', icon: <IconApex /> },
    ],
  },
  {
    title: 'Logs',
    items: [
      { to: '/logs/parameters', label: 'Parameter Logs', icon: <IconChart /> },
      { to: '/logs/alarms', label: 'Alarms', icon: <IconBell /> },
    ],
  },
  {
    title: 'Tools',
    items: [
      { to: '/stock-solutions', label: 'Stock Solutions', icon: <IconFlask /> },
      { to: '/dosing-schedule', label: 'Dosing Schedule', icon: <IconDroplet /> },
      { to: '/test-kits', label: 'Test Kits', icon: <IconBeaker /> },
      { to: '/coral-placer', label: 'Coral Placer', icon: <IconCoral /> },
    ],
  },

  {
    title: 'Planner',
    items: [
      { to: '/calendar', label: 'Calendar', icon: <IconCalendar /> },
      { to: '/notes', label: 'Journal', icon: <IconNote /> },
      { to: '/tasks', label: 'Tasks', icon: <IconTasks /> },
    ],
  },
  {
    title: 'Misc',
    items: [
      { to: '/pool', label: 'Pool & Spa', icon: <IconPool /> },
      { to: '/totm', label: 'TOTM Archive', icon: <IconArchive /> },
    ],
  },

  {
    title: 'Setup',
    items: [
      { to: '/settings', label: 'Settings', icon: <IconSettings /> },
      { to: '/dev', label: 'Dev', icon: <IconDev /> },
    ],
  },
];
